import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import EditableSection from '../../../components/EditableSection';
import EquipmentChecklistEditor from '../../../components/equipment/EquipmentChecklistEditor';
import useEditableDraft from '../../../hooks/useEditableDraft';
import {
    useGetStationProductDefaultsQuery,
    useUpdateStationProductDefaultsMutation,
} from '../../../api/inventory/inventoryApiSlice';
import type {
    EquipmentItem,
    StationProductDefaultsRow,
} from '../../../api/station/stationDefaultsApiTypes';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import { errorMessageFrom } from '../../../utils/errorMessage';
import {
    ChecklistRow,
    checklistComplete,
    checklistKeys,
    checklistLabels,
    fromChecklistRows,
    hasEquipmentCheck,
    limitText,
    parseLimitInput,
    toChecklistRows,
} from '../../../utils/stationDefaults';

const fieldClass =
    'w-full border border-foreground/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary';

/** One unit's draft: per value, whether the station has its own, and that value. */
interface Draft {
    ownLimit: boolean;
    limit: string;
    ownChecklist: boolean;
    checklist: ChecklistRow[];
}

const draftOf = (row: StationProductDefaultsRow): Draft => ({
    ownLimit: row.maxLimit.own !== null,
    limit: limitText(row.maxLimit.own),
    ownChecklist: row.equipment.own !== null,
    checklist: toChecklistRows(row.equipment.own),
});

/**
 * What a draft would store: the own value where "own" is picked, null
 * ("inherit") where it is not. What was typed under a choice that is no longer
 * picked does not count, so own → type 5 → back to inherit is not a change.
 * An own limit that does not parse stays as its text: a change, and not savable.
 *
 * `reserved`: the keys of the row's stored lists. A key removed in this edit is
 * never handed to a new item (issue reports match flagged items by key), so
 * "remove Board, add Board" is a new item, and a change.
 */
const storedOf = (draft: Draft, reserved: string[]) => {
    const limit = parseLimitInput(draft.limit);
    const ownLimit = limit.ok ? limit.value : draft.limit;
    return {
        maxLimit: draft.ownLimit ? ownLimit : null,
        equipment: draft.ownChecklist ? fromChecklistRows(draft.checklist, reserved) : null,
    };
};

interface RowProps {
    stationId: string;
    row: StationProductDefaultsRow;
    readOnly: boolean;
}

/**
 * One product row of the station: its per-booking limit and checklist, each
 * either inherited from the product or the station's own (D15, product
 * refactor 4.4). Bearbeiten → Speichern / Abbrechen; nothing saves on a pick.
 */
export const StationProductDefaultsRowSection = ({ stationId, row, readOnly }: RowProps) => {
    const { t, i18n } = useTranslation();
    const saved = draftOf(row);
    const reserved = useMemo(
        () => checklistKeys(row.equipment.own, row.equipment.product),
        [row.equipment.own, row.equipment.product],
    );
    const draftEquals = useCallback(
        (a: Draft, b: Draft) => JSON.stringify(storedOf(a, reserved)) === JSON.stringify(storedOf(b, reserved)),
        [reserved],
    );
    const { editing, value, base, setDraft, isDirty, startEdit, cancel, finish } = useEditableDraft(saved, draftEquals);
    const [update, { isLoading: saving }] = useUpdateStationProductDefaultsMutation();
    const [error, setError] = useState<string | null>(null);
    const offersChecklist = hasEquipmentCheck(row.useCase);
    const label = (key: string, options?: Record<string, unknown>) =>
        t(`station_detail.product_defaults.${key}`, options ?? {});

    const limitInput = parseLimitInput(value.limit);
    // An own limit needs a value: an empty own limit would be "inherit", which
    // is the other choice.
    const limitValid = !value.ownLimit || (limitInput.ok && limitInput.value !== null);
    const checklistValid = !value.ownChecklist || checklistComplete(value.checklist);

    const limitView = (limit: number | null) => (limit === null ? label('no_limit') : String(limit));
    const checklistView = (items: EquipmentItem[] | null) => {
        if (items === null || items.length === 0) return label('no_checklist');
        return checklistLabels(items, i18n.language).join(', ');
    };

    const onSave = async () => {
        setError(null);
        // Only what changed against what this edit started from (`base`).
        const next = storedOf(value, reserved);
        const from = storedOf(base, reserved);
        const body: { maxLimit?: number | null, equipment?: EquipmentItem[] | null } = {};
        if (JSON.stringify(next.maxLimit) !== JSON.stringify(from.maxLimit)) {
            body.maxLimit = value.ownLimit && limitInput.ok ? limitInput.value : null;
        }
        if (offersChecklist && JSON.stringify(next.equipment) !== JSON.stringify(from.equipment)) {
            body.equipment = next.equipment;
        }
        try {
            await update({ stationId, groupId: row.groupId, ...body }).unwrap();
            finish();
        } catch (err) {
            setError(errorMessageFrom(err, label('save_error'), t));
        }
    };

    const choice = (
        name: string,
        own: boolean,
        inheritedText: string,
        onPick: (own: boolean) => void,
    ) => (
        <div className={'flex flex-col gap-1'} role={'radiogroup'}>
            <label className={'flex items-center gap-2 text-sm text-foreground'}>
                <input
                    type={'radio'}
                    className={'accent-primary'}
                    name={`${row.groupId}-${name}`}
                    checked={!own}
                    onChange={() => onPick(false)}
                />
                {label('inherit_option', { value: inheritedText })}
            </label>
            <label className={'flex items-center gap-2 text-sm text-foreground'}>
                <input
                    type={'radio'}
                    className={'accent-primary'}
                    name={`${row.groupId}-${name}`}
                    checked={own}
                    onChange={() => onPick(true)}
                />
                {label('own_option')}
            </label>
        </div>
    );

    const viewLine = (title: string, own: boolean, text: string) => (
        <div className={'flex flex-col gap-0.5'}>
            <span className={'text-sm text-foreground/60'}>{title}</span>
            <span className={'text-sm text-foreground'}>{text}</span>
            <span className={'text-xs text-foreground/60'}>
                {own ? label('own_value') : label('inherited')}
            </span>
        </div>
    );

    // The view reads the hook's value: right after a save it is what was
    // stored, until the refetch brings the server's (`useEditableDraft`). An
    // inherited value is the product's (the resolver: own, else product).
    const stored = storedOf(value, reserved);
    const shownLimit = value.ownLimit && typeof stored.maxLimit === 'number'
        ? { own: true, value: stored.maxLimit }
        : { own: false, value: row.maxLimit.product };
    const shownChecklist = value.ownChecklist
        ? { own: true, value: stored.equipment }
        : { own: false, value: row.equipment.product };

    const shared = row.sharedWithStations.length > 0
        ? label('shared_with', { stations: row.sharedWithStations.map((s) => s.name ?? s.id).join(', ') })
        : undefined;

    return (
        <EditableSection
            title={row.productName ?? label('unnamed_product')}
            description={shared}
            editing={editing}
            readOnly={readOnly}
            isDirty={isDirty}
            saving={saving}
            canSave={limitValid && checklistValid}
            error={error}
            onEdit={() => {
                setError(null);
                startEdit();
            }}
            onCancel={() => {
                setError(null);
                cancel();
            }}
            onSave={onSave}
            view={(
                <div className={'flex flex-col gap-3'}>
                    {viewLine(label('limit_label'), shownLimit.own, limitView(shownLimit.value))}
                    {offersChecklist && viewLine(
                        label('checklist_label'),
                        shownChecklist.own,
                        checklistView(shownChecklist.value),
                    )}
                </div>
            )}
        >
            <div className={'flex flex-col gap-2'}>
                <span className={'text-sm font-medium text-foreground'}>{label('limit_label')}</span>
                {choice('limit', value.ownLimit, limitView(row.maxLimit.product), (own) => setDraft({ ...value, ownLimit: own }))}
                {value.ownLimit && (
                    <>
                        <input
                            type={'text'}
                            inputMode={'numeric'}
                            aria-label={label('limit_input')}
                            className={fieldClass}
                            value={value.limit}
                            onChange={(e) => setDraft({ ...value, limit: e.target.value })}
                        />
                        {!limitValid && (
                            <p className={'text-sm text-foreground'}>{label('limit_invalid')}</p>
                        )}
                    </>
                )}
            </div>
            {offersChecklist && (
                <div className={'flex flex-col gap-2'}>
                    <span className={'text-sm font-medium text-foreground'}>{label('checklist_label')}</span>
                    {choice(
                        'checklist',
                        value.ownChecklist,
                        checklistView(row.equipment.product),
                        (own) => setDraft({
                            ...value,
                            ownChecklist: own,
                            // An own list starts from the product's, the usual
                            // starting point for a station that differs a little.
                            checklist: own && value.checklist.length === 0 && !saved.ownChecklist
                                ? toChecklistRows(row.equipment.product)
                                : value.checklist,
                        }),
                    )}
                    {value.ownChecklist && (
                        <>
                            <p className={'text-xs text-foreground/60'}>{label('own_checklist_hint')}</p>
                            <EquipmentChecklistEditor
                                idPrefix={`station-equipment-${row.groupId}`}
                                rows={value.checklist}
                                onChange={(rows) => setDraft({ ...value, checklist: rows })}
                            />
                            {!checklistValid && (
                                <p className={'text-sm text-foreground'}>{t('equipment_checklist.labels_required')}</p>
                            )}
                        </>
                    )}
                </div>
            )}
        </EditableSection>
    );
};

interface Props {
    stationId: string;
}

/**
 * The station's products with the limit and checklist that apply here: the
 * product's default (Services → product), or this station's own. One unit per
 * product row.
 */
const StationProductDefaultsSection = ({ stationId }: Props) => {
    const { t } = useTranslation();
    const canWrite = useSelector(makeHasPermissionSelector(Permission.writeDepots()));
    // The product default is edited in another slice, which tags cannot reach.
    const { data, isLoading, isError } = useGetStationProductDefaultsQuery(
        { stationId },
        { refetchOnMountOrArgChange: true },
    );

    let body;
    if (isLoading) {
        body = <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>;
    } else if (isError) {
        // A failure says so; it never reads as "this station has no products".
        body = <p className={'text-sm text-foreground'}>{t('station_detail.product_defaults.load_error')}</p>;
    } else if (!data || data.rows.length === 0) {
        return null;
    } else {
        body = data.rows.map((row) => (
            <StationProductDefaultsRowSection
                key={row.groupId}
                stationId={stationId}
                row={row}
                readOnly={!canWrite}
            />
        ));
    }

    return (
        <div className={'flex flex-col gap-4'}>
            <div>
                <h2 className={'text-lg font-semibold text-foreground'}>
                    {t('station_detail.product_defaults.title')}
                </h2>
                <p className={'text-sm text-foreground/60'}>{t('station_detail.product_defaults.subtitle')}</p>
            </div>
            {body}
        </div>
    );
};

export default StationProductDefaultsSection;
